import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  RowCustomAction
} from '@globaleris/ui.library'
import { Typography } from '@material-ui/core'
import { Edit } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { TaskModel } from '../../../../api/Argentia/endpoints/tasks/model/tasks'
import { EnumCriticidad } from '../../../../api/oData/enums/enumCriticidad'
import { EnumDominioTarea } from '../../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoTarea } from '../../../../api/oData/enums/enumEstadoTarea'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../../../helpers'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import { HttpErrorProps } from '../../../../hooks'
import { Pages } from '../../../../model/RBAC/constants'
import {
  DataTableFilter,
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../../redux'

interface TasksTableCardProps {
  show: boolean
  title?: string
  data: TaskModel[]
  isLoading: boolean
  onTableEditClick: (selectedRow: TaskModel) => void
  onTableAddClick?: () => void
  dataTableFilter: DataTableFilter | undefined
  dataTableSearch: string | undefined
  dataTableErrorProps: (HttpErrorProps & { title?: string }) | undefined
}

export const TasksTableCard: React.FC<TasksTableCardProps> = ({
  show = false,
  title,
  data,
  isLoading,
  onTableEditClick,
  onTableAddClick,
  dataTableFilter,
  dataTableSearch,
  dataTableErrorProps
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'tasks', 'enums'])
  const history = useHistory()

  // #region Handlers Starts
  // #endregion Handlers Ends

  // #region Functions Starts
  const getRowCustomActions = () => {
    const actions: RowCustomAction[] = [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('tasks:components.taskTableCard.table.tooltips.taskEdit'),
        onClick: (selectedRow: TaskModel) => {
          onTableEditClick(selectedRow)
        }
      }
    ]

    return actions
  }

  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'id',
      label: t('tasks:components.taskTableCard.table.columns.id'),
      type: DataType.TEXT
    },
    // {
    //   name: 'taskType.code',
    //   label: t('tasks:components.taskTableCard.table.columns.tasktype'),
    //   type: DataType.TEXT
    // },
    {
      name: 'taskType.description',
      label: t('tasks:components.taskTableCard.table.columns.tasktype'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'creationDate',
      label: t('tasks:components.taskTableCard.table.columns.creationDate'),
      type: DataType.TEXT
    },
    {
      name: 'criticality',
      label: t('tasks:components.taskTableCard.table.columns.criticality'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumCriticidad, 'enums:EnumCriticidad')
    },
    {
      name: 'description',
      label: t('tasks:components.taskTableCard.table.columns.description'),
      type: DataType.TEXT,
      columnWidth: 300
    },
    {
      name: 'taskType.domain',
      label: t('tasks:components.taskTableCard.table.columns.domain'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumDominioTarea,
        'enums:EnumDominioTarea',
        [
          EnumDominioTarea.Documento,
          EnumDominioTarea.Tramitacion,
          EnumDominioTarea.Factura,
          EnumDominioTarea.Lead,
          EnumDominioTarea.EjecucionCF,
          EnumDominioTarea.PortalClientesFactura,
          EnumDominioTarea.PortalClientesContrato
        ]
      )
    },
    {
      name: 'assignedUser',
      label: t('tasks:components.taskTableCard.table.columns.assignedUser'),
      type: DataType.TEXT
    },
    {
      name: 'closingDate',
      label: t('tasks:components.taskTableCard.table.columns.closingDate'),
      type: DataType.TEXT
    },
    {
      name: 'status',
      label: t('tasks:components.taskTableCard.table.columns.status'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoTarea, 'enums:EnumEstadoTarea')
    }
  ]

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    filterType: FilterType.ADVANCED,
    elevation: 0,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,
    onRowInsert: async (row: any) => {
      onTableAddClick && onTableAddClick()
      return true
    },
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('tasks:components.taskTableCard.table.analytics')
    },
    rowCustomActions: getRowCustomActions(),
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <CardWithTransition show={show}>
      {isLoading ? (
        <DataTableSkeleton rows={5} elevation={0} />
      ) : (
        <DataTable
          title={
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          }
          columns={columns}
          options={options}
          data={data}
          info={DataTableErrorHandler(dataTableErrorProps)}
          userPermissions={getUserPermissions(Pages.Tasks)}
          appliedFilter={dataTableFilter}
          appliedSearch={dataTableSearch}
        />
      )}
    </CardWithTransition>
  )
}
